import React from "react";
import { Button, Dialog } from "@/ds";
import { Icon } from "@/lib";
import { useT } from "../i18n";
import styles from "./working-address.module.css";

/**
 * The address a business receives customer enquiries at, and what to do with it (#1229).
 *
 * The same content in two shapes: a dialog the moment the address exists, when nobody has asked
 * yet and the whole point is to say it before they wonder, and a section in Settings for the
 * person who comes back later asking "what is our address".
 *
 * `hosted` changes the text, not the layout. An address on our mail server was chosen once, cannot
 * be renamed, and is not a mailbox anyone signs into — three promises that are simply untrue of an
 * address the business already owned and brought with them.
 */

/** The address, and the only action this surface offers: take it away with you. */
export function AddressWithCopy({ address }: { address: string }) {
  const t = useT();
  const [copied, setCopied] = React.useState(false);
  // Not every context has a clipboard: an insecure origin, an old browser, a webview. A button
  // that cannot work is worse than no button, so it is absent rather than disabled or silent.
  const canCopy = typeof navigator !== "undefined" && Boolean(navigator.clipboard?.writeText);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(address);
      setCopied(true);
    } catch {
      // The address is plain selectable text either way; saying "copied" when it was not would be
      // the only real failure here.
      setCopied(false);
    }
  };

  return (
    <div className={styles.hero} data-testid="working-address">
      <p className={styles.value}>{address}</p>
      {canCopy && (copied
        ? (
          <span className={styles.copied} data-testid="working-address-copied">
            <Icon name="check" size={14} />
            {t("workingAddress.copied")}
          </span>
        )
        : (
          <Button
            variant="secondary"
            size="sm"
            iconLeft={<Icon name="clipboard" size={14} />}
            onClick={() => { void copy(); }}
          >
            {t("workingAddress.copy")}
          </Button>
        ))}
    </div>
  );
}

const PLACES = [
  { key: "workingAddress.placeWebsite", icon: "external" },
  { key: "workingAddress.placeQuotes", icon: "receipt" },
  { key: "workingAddress.placeSignature", icon: "pencil" },
] as const;

/** What the address is for. Text only — it grants nothing and asks for nothing. */
export function WorkingAddressInstructions({ hosted }: { hosted: boolean }) {
  const t = useT();
  return (
    <div className={styles.section} data-testid="working-address-instructions">
      <p className={styles.lead}>{t("workingAddress.lead")}</p>
      <ul className={styles.places}>
        {PLACES.map((place) => (
          <li className={styles.place} key={place.key}>
            <span className={styles.placeIcon} aria-hidden="true">
              <Icon name={place.icon} size={14} />
            </span>
            <span>{t(place.key)}</span>
          </li>
        ))}
      </ul>
      <div className={styles.notes}>
        <p className={styles.note}>{t("workingAddress.arrives")}</p>
        {hosted && <p className={styles.note}>{t("workingAddress.forward")}</p>}
        {hosted && <p className={styles.note}>{t("workingAddress.notAMailbox")}</p>}
      </div>
    </div>
  );
}

/**
 * Shown once, right after the address comes into existence. A dialog rather than a banner because
 * this is the one moment the person has nothing else to do and everything to gain from reading it;
 * a strip above a form they are still filling in would be scrolled past.
 */
export function WorkingAddressDialog({
  open,
  address,
  hosted,
  onClose,
}: {
  open: boolean;
  address: string;
  hosted: boolean;
  onClose: () => void;
}) {
  const t = useT();
  return (
    <Dialog
      open={open}
      onClose={onClose}
      wide
      tone="navy"
      icon={<Icon name="mail" size={20} />}
      title={t("workingAddress.title")}
      footer={
        <div className="management-actions management-actions--end">
          <Button variant="primary" onClick={onClose}>{t("workingAddress.done")}</Button>
        </div>
      }
    >
      {/* The dialog itself takes no test id — the design system renders only the props it knows —
          so the body carries it. */}
      <div className={styles.dialogBody} data-testid="working-address-dialog">
        <AddressWithCopy address={address} />
        <WorkingAddressInstructions hosted={hosted} />
      </div>
    </Dialog>
  );
}
